import { EMPTY, Observable } from 'rxjs';
import {
  IApiListResponse,
  IDocumentOperationOptions,
  IFilterQuery,
  ObjectId,
} from '@shared/types';
import { get, omit, range, assign, size } from 'lodash';
import { rx } from '@shared/rx';
import { MAX_PAGE_SIZE } from '@config/global-constants';

// eslint-disable-next-line @typescript-eslint/naming-convention,no-underscore-dangle,id-blacklist,id-match, prefer-arrow/prefer-arrow-functions
export const FetchFullDataDecorator = function (): (
  target,
  propertyKey: string,
  descriptor: PropertyDescriptor,
) => PropertyDescriptor {
  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  return function (
    target,
    propertyKey: string,
    descriptor: PropertyDescriptor,
  ): PropertyDescriptor {
    const original = descriptor.value;

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    const apiRequest = function (
      query?: IFilterQuery,
      options: IDocumentOperationOptions = {},
    ): Observable<any> {
      // eslint-disable-next-line no-invalid-this
      return original.call(this, query, options).pipe(
        rx.mergeMap(async (response: IApiListResponse) => {
          const pageSize = response.page_size;
          const totalItems = response.total;
          if (pageSize >= totalItems || !options.fetchFullListData) {
            return response;
          }

          const adjustedResponse = { ...response };
          const numberOfRequests = Math.ceil(totalItems / pageSize);
          const additionalRequestParams = range(1, numberOfRequests).map(
            (v) => ({
              skip: pageSize * v,
            }),
          );

          const initialQuery = query;

          for (const requestParam of additionalRequestParams) {
            const additionalQuery = {
              ...initialQuery,
              ...requestParam,
            };
            const additionalResponse = await original
              // eslint-disable-next-line no-invalid-this
              .call(this, additionalQuery, omit(options, 'fetchFullListData'))
              .toPromise();
            const additionalItems = get(additionalResponse, 'items', []);
            adjustedResponse.items = [
              ...adjustedResponse.items,
              ...additionalItems,
            ];
          }

          return adjustedResponse;
        }),
      );
    };

    return {
      ...descriptor,
      value: apiRequest,
    };
  };
};

// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match, prefer-arrow/prefer-arrow-functions
export const FetchCurrentFullDataDecorator = function (): (
  target,
  propertyKey: string,
  descriptor: PropertyDescriptor,
) => PropertyDescriptor {
  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  return function (
    target,
    propertyKey: string,
    descriptor: PropertyDescriptor,
  ): PropertyDescriptor {
    const original = descriptor.value;

    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    const apiRequest = function (
      query: IFilterQuery = {},
      siteIds: ObjectId[] = [],
      siteGroupId: ObjectId = null,
      options: IDocumentOperationOptions = {},
    ): Observable<any> {
      const fetchFullListData = get(options, 'fetchFullListData', true);
      const params = { ...query };
      if (fetchFullListData) {
        params.skip = 0;
        params.top = MAX_PAGE_SIZE;
      }
      // eslint-disable-next-line no-invalid-this, @typescript-eslint/no-this-alias
      const $this = this;

      return original.call($this, params, siteIds, siteGroupId, options).pipe(
        rx.expand((res) => {
          if (size(get(res, 'items')) < params.top || !fetchFullListData) {
            return EMPTY;
          }
          params.skip += params.top;

          return original.call($this, params, siteIds, siteGroupId, options);
        }),
        rx.reduce(
          (totalResult: IApiListResponse, currentResult: IApiListResponse) => {
            if (!fetchFullListData) {
              return currentResult;
            }
            totalResult.items.push(...get(currentResult, 'items', []));
            totalResult.total += size(get(currentResult, 'items', []));
            assign(totalResult, omit(currentResult, ['total', 'items']));

            return totalResult;
          },
          <IApiListResponse>{ items: [], total: 0 },
        ),
      );
    };

    return {
      ...descriptor,
      value: apiRequest,
    };
  };
};
